<script lang="ts" setup>
import { PropType, ref, computed, watch } from "vue";
import {
  Tooltip,
  DocumentUploadModal,
  LoadingSpinner,
  Dropdown,
  PromomatsImporter,
  UrlBasedDocumentImporter,
} from "@/components";
import ThumbnailReplaceModal from "./ThumbnailReplaceModal.vue";
import { WorkspaceDocumentFragment } from "@/graphql/operations";
import { getStorageProxyUrl, MimeType } from "@/util";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

const store = useStore();
const { t } = useI18n();

const props = defineProps({
  doc: {
    type: Object as PropType<WorkspaceDocumentFragment>,
    required: true,
  },
  isProcessing: {
    type: Boolean,
    required: true,
  },
  hasProcessingError: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["success", "thumbnailReplaced"]);

const uploaderActive = ref<boolean>(false);
const promomatsImporterActive = ref(false);
const urlBasedImporterActive = ref(false);
const thumbnailReplaceActive = ref(false);
const processingErrorDismissed = ref(false);

watch(
  () => props.hasProcessingError,
  (hasProcessingError, hadProcessingError) => {
    if (hasProcessingError && !hadProcessingError) {
      processingErrorDismissed.value = false;
    }
  },
);

const mayAdministrate = computed(() =>
  Boolean(store.getters.currentWorkspace?.root?.myPrivileges?.mayAdministrate),
);

const fileUpdateTooltip = computed(() => {
  if (MimeType.isAudio(props.doc.originalMimeType)) {
    return t("pvDocEditor.fileUpdate.audioTooltip");
  }

  if (MimeType.isVideo(props.doc.mimeType)) {
    return t("pvDocEditor.fileUpdate.videoTooltip");
  }

  return t("pvDocEditor.fileUpdate.tooltip");
});

const displayUploader = () => {
  uploaderActive.value = true;
};

const closeUploader = () => {
  uploaderActive.value = false;
};

const uploadActions = computed(() => {
  const items = [];
  items.push({ title: t("workspace.uploadLocalDevice"), value: "upload" });

  if (store.getters.user.hasPromomatsImport()) {
    items.push({
      title: t("workspace.uploadFromPromomats"),
      value: "promomats_import",
    });
  }

  if (props.doc.workspace?.mayUseUrlBasedDocumentImport) {
    items.push({
      title: t("workspace.urlBasedUpload"),
      value: "url_based_import",
    });
  }

  return items;
});

function handleUploadAction(value: string) {
  switch (value) {
    case "upload":
      uploaderActive.value = true;
      break;

    case "promomats_import":
      promomatsImporterActive.value = true;
      break;

    case "url_based_import":
      urlBasedImporterActive.value = true;
      break;
  }
}

const displayThumbnailReplacer = () => {
  thumbnailReplaceActive.value = true;
};
</script>

<template>
  <div class="file_update">
    <div v-if="isProcessing">
      <div>
        <LoadingSpinner :color="'var(--classic-text, black)'" />
      </div>
      Processing...
    </div>

    <template v-else>
      <div
        v-if="hasProcessingError && !processingErrorDismissed"
        class="message error processing_error"
        data-test="processing-error"
        role="alert"
      >
        <span>{{
          $t("pvDocEditor.fileUpdate.processingFailure.message")
        }}</span>
        <button
          type="button"
          class="processing_error_dismiss"
          :aria-label="$t('pvDocEditor.fileUpdate.processingFailure.dismiss')"
          data-test="dismiss-processing-error"
          @click="processingErrorDismissed = true"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <div class="container">
        <div
          class="thumbnail"
          :style="{
            'background-image': `url(${getStorageProxyUrl(doc.thumbnailDownloadUrl)})`,
          }"
        ></div>

        <div class="action_icons">
          <div class="upload_icon">
            <tooltip class="bottom" v-if="uploadActions.length === 1">
              <template v-slot>
                <button
                  type="button"
                  class="icon_action"
                  :aria-label="fileUpdateTooltip"
                  data-test="upload-ctrl"
                  @click="displayUploader"
                >
                  <span class="material-symbols-outlined icon">
                    upload_file
                  </span>
                </button>
              </template>
              <template #tooltip>{{ fileUpdateTooltip }}</template>
            </tooltip>
            <dropdown
              v-else-if="uploadActions.length > 1"
              @change="handleUploadAction"
              :items="uploadActions"
            >
              <template v-slot>
                <tooltip>
                  <template v-slot>
                    <span
                      class="material-symbols-outlined icon clickable"
                      data-test="upload-ctrl"
                      :aria-label="fileUpdateTooltip"
                    >
                      upload_file
                    </span>
                  </template>
                  <template #tooltip>{{ fileUpdateTooltip }}</template>
                </tooltip>
              </template>
            </dropdown>
          </div>

          <div class="thumbnail_icon" v-if="mayAdministrate">
            <tooltip class="bottom">
              <template v-slot>
                <button
                  type="button"
                  class="icon_action"
                  :aria-label="$t('pvDocEditor.thumbnailReplace.tooltip')"
                  data-test="replace-thumbnail-ctrl"
                  @click="displayThumbnailReplacer"
                >
                  <span class="material-symbols-outlined icon">
                    add_photo_alternate
                  </span>
                </button>
              </template>
              <template #tooltip>
                {{ $t("pvDocEditor.thumbnailReplace.tooltip") }}
              </template>
            </tooltip>
          </div>
        </div>
      </div>
      <div class="img_subtitle">
        {{ doc.fileName }}
      </div>
    </template>

    <DocumentUploadModal
      v-if="uploaderActive"
      :active="uploaderActive"
      :doc="doc"
      :singleFile="true"
      @success="emit('success')"
      @close="closeUploader"
    >
    </DocumentUploadModal>
    <PromomatsImporter
      v-if="promomatsImporterActive"
      @close="promomatsImporterActive = false"
      :doc="doc"
      @success="emit('success')"
    />

    <UrlBasedDocumentImporter
      v-if="urlBasedImporterActive"
      @close="urlBasedImporterActive = false"
      @success="emit('success')"
      :doc="doc"
    />

    <ThumbnailReplaceModal
      v-if="thumbnailReplaceActive"
      :active="thumbnailReplaceActive"
      :doc="doc"
      @success="emit('thumbnailReplaced')"
      @close="thumbnailReplaceActive = false"
    />
  </div>
</template>

<style lang="scss" scoped>
.file_update .container {
  position: relative;
  height: 104px;
}

.processing_error {
  align-items: flex-start;
  display: flex;
  gap: 0.75em;
  justify-content: space-between;
  margin-bottom: 1em;
}

.processing_error_dismiss {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.thumbnail {
  height: 100px;
  width: 150px;
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;
  float: left;
  border: 1px solid var(--classic-border-strong, #cecece);
  box-sizing: border-box;
  border-radius: 2px;
}

.action_icons {
  position: absolute;
  /* Center on the right edge of the 150px-wide thumbnail */
  left: 150px;
  top: 50px;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
  z-index: 99;
}

.icon_action {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
}

.icon {
  color: var(--classic-accent, #34b5ba);
  font-size: 1em;
  height: 28px;
  width: 28px;
  line-height: 1.5em;
  text-align: center;
  box-sizing: border-box;
  border: 1px solid var(--classic-accent, #34b5ba);
  border-radius: 50%;
  cursor: pointer;
  background: var(--classic-surface, #fff);
  z-index: 99;
}

.img_subtitle {
  font-size: 0.9rem;
  margin-top: 0.6em;
  font-weight: 500;
}
</style>
